import clsx from 'clsx'
import countries from 'flag-icons/country.json'
import { useSearchParams } from 'next/navigation'
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { type Control, type FieldValues, SubmitHandler, useForm } from 'react-hook-form'

import Button from 'lib/components/Button/Button'
import PasswordInput from 'lib/components/Form/PasswordInput/PasswordInput'
import PhoneInput from 'lib/components/Form/PhoneInput/PhoneInput'
import SelectInput from 'lib/components/Form/SelectInput/SelectInput'
import TextInput from 'lib/components/Form/TextInput/TextInput'
import Icon from 'lib/components/Icon/Icon'
import Link from 'lib/components/Link/Link'
import Notification from 'lib/components/Notification/Notification'

import { useUserApi } from '@/api/useUserApi'
import { useGuestUserUpdateApi } from '@/app/book/api/useGuestUserUpdateApi'
import useCartStore from '@/app/book/stores/useCartStore'
import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'
import useSessionStore from '@/stores/useSessionStore'

interface CreateAccountFormValues extends FieldValues {
  firstName: string
  lastName: string
  email: string
  password: string
  country: string
  phone: string
}

type StatusMessage = {
  type: 'success' | 'error'
  message: string
}

type CountryRecord = {
  code: string
  name: string
}

const BENEFITS = [
  'Save payment info for faster checkout.',
  'Check order status & see purchase history.',
  'Save gift cards and get special offers.',
]

const countryOptions = (countries as CountryRecord[]).map(({ code, name }) => ({
  value: code,
  label: name,
}))

const BulletItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-center gap-3">
    <Icon icon="check" className="size-6 text-accessible-green" />
    <p className="text-neutral-700 type-body-8">{children}</p>
  </li>
)

type CreateAccountUser = {
  first_name?: string
  last_name?: string
  email?: string
  country_code?: string
  phone_number?: string
}

const CreateAnAccountSection = ({
  user,
}: { user?: CreateAccountUser } = {}): JSX.Element | null => {
  const searchParams = useSearchParams()
  const guestUserId = useMemo(() => searchParams?.get('guest_id') ?? '', [searchParams])
  const order = useCartStore((state) => state.order)
  const userRoles = useSessionStore((state) => state.userRoles)
  const hasSessionHydrated = useSessionStore((state) => state.hasHydrated)
  const authToken = useSessionStore((state) => state.authToken)
  const userId = useSessionStore((state) => state.userId)
  const userEmail = useSessionStore((state) => state.userEmail)
  const setAuth = useSessionStore((state) => state.setAuth)
  const isGuestUser = userRoles.includes('guest_user')
  const { track } = useRudderstackAnalytics()
  const { mutateAsync: updateGuestUser, isPending } = useGuestUserUpdateApi()
  const userQuery = useUserApi(!!authToken)

  const [status, setStatus] = useState<StatusMessage | null>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [stayVisibleAfterSuccess, setStayVisibleAfterSuccess] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const queryUser = userQuery.data

  // The second check is to get the user object for anonymous users
  // (users without an authenticated session) as the stores would be empty
  // Guest users carry a placeholder email on their backend profile
  // (e.g. ...@placeholder.com) that GetCurrentUser returns. The real email the
  // customer entered lives in session-store (userEmail) — set by the book flow
  // on guest-update and bridged in by the cruises booking app on redirect — so
  // it must take precedence over queryUser/user to avoid showing the placeholder.
  // Prefer the order's user (traveler details from Step 3) over the
  // authenticated-profile query.  For returning-guest bookings the backend
  // profile may still carry placeholder values ("Guest", "User",
  // "0000000000") when the email was previously used but never claimed.
  const defaultValues = useMemo<CreateAccountFormValues>(() => {
    return {
      firstName: user?.first_name || queryUser?.firstName || '',
      lastName: user?.last_name || queryUser?.lastName || '',
      email: userEmail || queryUser?.email || user?.email || '',
      password: '',
      country: (user?.country_code || 'us').toLowerCase(),
      phone: user?.phone_number || queryUser?.phoneNumber || '',
    }
  }, [
    queryUser?.firstName,
    queryUser?.lastName,
    queryUser?.email,
    queryUser?.phoneNumber,
    userEmail,
    user?.first_name,
    user?.last_name,
    user?.email,
    user?.country_code,
    user?.phone_number,
  ])

  const emailValue = useMemo(
    () => userEmail || queryUser?.email || user?.email || '',
    [userEmail, queryUser?.email, user?.email],
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<CreateAccountFormValues>({
    mode: 'onBlur',
    defaultValues,
  })

  const formControl = control as unknown as Control

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const onSubmit = useCallback<SubmitHandler<CreateAccountFormValues>>(
    async (values) => {
      setStatus(null)

      try {
        const response = await updateGuestUser({
          claimUser: true,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: emailValue,
          phoneNumber: values.phone,
          countryCode: values.country ? values.country.toUpperCase() : 'US',
          password: values.password,
          guestUserId,
        })

        const updatedRoles = userRoles.filter((role) => role !== 'guest_user')

        setStayVisibleAfterSuccess(true)
        setAuth(
          authToken ?? response.authToken ?? null,
          queryUser?.id ?? userId ?? response.user?.id ?? null,
          queryUser?.email ?? userEmail ?? response.user?.email ?? null,
          updatedRoles ?? [],
        )
        setStatus({ type: 'success', message: 'Account created! You are now signed in.' })
        setSubmitted(true)
        track?.('order_confirmation_create_account', { order_id: order?.order_id })
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to create your account right now. Please try again.'
        const alreadyClaimed = errorMessage.includes('User has already been claimed')

        setStatus({
          type: 'error',
          message: alreadyClaimed ? 'User has already been claimed.' : errorMessage,
        })
      }
    },
    [
      authToken,
      emailValue,
      guestUserId,
      order?.order_id,
      queryUser?.email,
      queryUser?.id,
      setAuth,
      track,
      updateGuestUser,
      userEmail,
      userId,
      userRoles,
    ],
  )

  if (!hasSessionHydrated || (!isGuestUser && !stayVisibleAfterSuccess && !guestUserId)) {
    return null
  }

  return (
    <section className="rounded-8 border !border-neutral-400 p-[30px]" aria-live="polite">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <h2 className="mt-1 text-neutral-900 type-body-1-desktop">
          Create an account for a faster checkout next time.
        </h2>
        <button
          type="button"
          aria-controls="create-account-section-body"
          aria-expanded={!isCollapsed}
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="self-start rounded-full p-2 transition"
        >
          <Icon
            icon="chevron-down"
            className={clsx(
              'size-8 text-neutral-700 transition-transform',
              !isCollapsed && '-rotate-90 md:-rotate-180',
            )}
          />
          <span className="sr-only">
            {isCollapsed ? 'Expand create account section' : 'Collapse create account section'}
          </span>
        </button>
      </div>

      {!isCollapsed && (
        <>
          {status && (
            <div className="mt-6">
              <Notification
                type={status.type === 'success' ? 'info' : 'urgent'}
                title={status.message}
              />
            </div>
          )}
          {!submitted && (
            <>
              <ul className="mt-2 space-y-3" id="create-account-section-body">
                {BENEFITS.map((copy) => (
                  <BulletItem key={copy}>{copy}</BulletItem>
                ))}
              </ul>

              <form className="mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="flex flex-col gap-6 md:flex-row">
                  <TextInput
                    control={formControl}
                    className="w-full"
                    name="firstName"
                    label="First name*"
                    placeholder="Jane"
                    rules={{ required: { value: true, message: 'First name is required' } }}
                    defaultValue={defaultValues.firstName}
                  />
                  <TextInput
                    control={formControl}
                    className="w-full"
                    name="lastName"
                    label="Last name*"
                    placeholder="Doe"
                    rules={{ required: { value: true, message: 'Last name is required' } }}
                    defaultValue={defaultValues.lastName}
                  />
                </div>

                <div className="flex flex-col gap-6 md:flex-row">
                  <TextInput
                    control={formControl}
                    className="w-full"
                    name="email"
                    disabled
                    label="Email address*"
                    placeholder={emailValue}
                    defaultValue={emailValue}
                  />
                  <PasswordInput
                    control={formControl}
                    className="w-full"
                    label="Password*"
                    name="password"
                    rules={{
                      required: { value: true, message: 'Password is required' },
                      minLength: { value: 6, message: 'Password is too short' },
                    }}
                    defaultValue={defaultValues.password}
                  />
                </div>

                <div className="flex flex-col gap-6 md:flex-row">
                  <PhoneInput
                    control={formControl}
                    className="w-full"
                    name="phone"
                    label="Phone number*"
                    placeholder="555-555-5555"
                    rules={{ required: { value: true, message: 'Phone number is required' } }}
                    defaultValue={defaultValues.phone}
                  />
                  <SelectInput
                    control={formControl}
                    className="w-full"
                    name="country"
                    label="Country of residence*"
                    rules={{ required: { value: true, message: 'Please select a country' } }}
                    options={countryOptions}
                    defaultValue={defaultValues.country}
                  />
                </div>

                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-col gap-2 md:flex-row">
                    <Button
                      className="!border-neutral-700 !text-neutral-700"
                      type="submit"
                      variant="secondary"
                      disabled={isPending || !isDirty}
                    >
                      {isPending ? 'Creating account...' : 'Create account'}
                    </Button>
                    <Link
                      className="text-black underline hover:no-underline"
                      onClick={(e) => {
                        e.preventDefault()
                        setIsCollapsed(true)
                      }}
                    >
                      Maybe next time
                    </Link>
                  </div>
                </div>
              </form>
            </>
          )}
        </>
      )}
    </section>
  )
}

export default CreateAnAccountSection
